import {
  formatGlobalActionDateISO,
  formatGlobalActionDateTime,
  formatGlobalActionTimeHM,
} from './builderDateTime.utils';
import { ipawsTemplateToFormData } from './ipawsActionPayload.utils';
import { toFemaEnvironmentClass } from 'features/Weather/features/WeatherAlerts/features/AlertCreator/utils/femaEnvironment.utils';

// The Global Actions service sets the IPAWS effective and expiration times from the interval
// (card BE-25). A saved draft or prepared message can still hold older values; the editor
// does not show them.
const withoutServiceOwnedTimes = ({ effective, expires, hasConfirmedExpires, ...formData } = {}) =>
  formData;

// The CAP status is the one send control (Mike, 2026-09-26). The editor keeps no approval state.
// A row stored as `approvalStatus: 'draft'` with a status other than Draft opens as Draft, and
// `savedAsDraft` makes the editor show why. Any other row keeps its stored status.
const withStoredIpawsStatus = (
  { approvalState, savedAsDraft, ...formData } = {},
  approvalStatus,
) =>
  approvalStatus === 'draft' && formData.status !== 'Draft'
    ? { ...formData, status: 'Draft', savedAsDraft: true }
    : formData;

// Multi-COG contract §1: a contractVersion 3 row stores its FEMA class as `params.environment`.
// A contractVersion 2 row has no class: it opens with `null`, and the save gate asks for one
// ("Select the FEMA environment."). The stored row is the source, not the raw builder data.
const readStoredIpawsEnvironment = (params) => toFemaEnvironmentClass(params?.environment);

// The serialized action is the save contract. rawDataList is only an editor snapshot and can be
// stale when an older client saved it, so the template wins for every CAP field on reopen.
//
// One editor decision is not in the CAP message: a person's review of a Spanish block
// (`isReviewed`). The snapshot keeps it. A block whose text is unchanged since the save reopens as
// reviewed; a block whose text differs reopens unreviewed.
const LANGUAGE_TEXT_KEYS = [
  'event',
  'headline',
  'description',
  'instruction',
  'areaDescription',
  'web',
  'contact',
  'weaShortText',
  'weaLongText',
];
const hasSameLanguageText = (a = {}, b = {}) =>
  LANGUAGE_TEXT_KEYS.every((key) => (a[key] ?? '') === (b[key] ?? ''));
const withSnapshotReview = (languages = [], snapshotLanguages = []) =>
  languages.map((language) => {
    const snapshot = snapshotLanguages.find(
      (candidate) => candidate?.language === language.language,
    );
    return snapshot && hasSameLanguageText(snapshot, language)
      ? { ...language, isReviewed: Boolean(snapshot.isReviewed) }
      : language;
  });
const withStoredIpawsTemplate = (formData = {}, params) => {
  if (!params?.template?.message) return formData;
  const template = ipawsTemplateToFormData(params.template);
  return {
    ...formData,
    ...template,
    languages: withSnapshotReview(template.languages, formData.languages),
  };
};

// Generates a random string ID
export const createNodeId = () => {
  return `ga_${crypto.randomUUID()}`;
};

// Create an empty condition object with a new ID and blank fields
export const createEmptyCondition = () => {
  return {
    id: createNodeId(),
    category: '',
    formData: {},
  };
};

export const getDefaultBuilderState = () => {
  return {
    metadata: {
      title: '',
      debounceTime: 0,
      minEventTime: 0,
    },
    conditions: [[createEmptyCondition()]], // IF, AND, OR conditions
    actions: [], // THEN actions
    until: [], // UNTIL conditions
  };
};

const safeParseRawDataList = (value) => {
  if (!value) return null;
  if (Array.isArray(value)) return value;
  if (typeof value !== 'string') return null;

  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : null;
  } catch (error) {
    return null;
  }
};

const normalizeSavedBuilderItem = (item, fallbackFactory) => {
  const fallback = fallbackFactory();

  if (!item || typeof item !== 'object') return fallback;

  return {
    ...fallback,
    ...item,
    id: item.id || fallback.id,
    category: item.category || '',
    formData: item.formData && typeof item.formData === 'object' ? item.formData : {},
  };
};

const normalizeSavedBuilderGroups = (groups) => {
  if (!Array.isArray(groups) || groups.length === 0) return null;

  const normalizedGroups = groups
    .map((group) => {
      if (!Array.isArray(group)) return [];

      return group.map((condition) => normalizeSavedBuilderItem(condition, createEmptyCondition));
    })
    .filter((group) => group.length > 0);

  return normalizedGroups.length > 0 ? normalizedGroups : null;
};

const normalizeSavedBuilderActions = (actions) => {
  if (!Array.isArray(actions)) return null;

  return actions.map((action) => normalizeSavedBuilderItem(action, createEmptyAction));
};

const getSavedBuilderState = (initialData) => {
  const rawDataList =
    safeParseRawDataList(initialData?.rawDataList) ??
    safeParseRawDataList(initialData?.raw_data_list);
  const savedData = rawDataList?.[0];

  if (!savedData || typeof savedData !== 'object') return null;

  const conditions = normalizeSavedBuilderGroups(savedData.conditions);
  const actions = normalizeSavedBuilderActions(savedData.actions);
  const until = normalizeSavedBuilderGroups(savedData.until);

  if (!conditions && !actions && !until) return null;

  return {
    conditions,
    actions,
    until,
  };
};

// Normalize a single condition based on its type and expected fields
const normalizeCondition = (condition) => {
  if (!condition || typeof condition !== 'object') return createEmptyCondition();

  switch (condition.type) {
    case 'datetime':
      return {
        id: createNodeId(),
        category: 'Date & Time',
        formData: {
          dateTime: formatGlobalActionDateTime(condition?.value?.value), // Format as YYYY-MM-DDTHH:MM
          liveNow: condition?.value?.liveNow === true,
        },
      };
    case 'date':
      return {
        id: createNodeId(),
        category: 'Date',
        formData: {
          date: formatGlobalActionDateISO(condition?.value?.value), // Format as YYYY-MM-DD
        },
      };
    case 'time':
      return {
        id: createNodeId(),
        category: 'Time',
        formData: {
          time: formatGlobalActionTimeHM(condition?.value?.value), // Format as HH:MM
        },
      };
    case 'device':
      return {
        id: createNodeId(),
        category: 'Device',
        formData: {
          deviceType: condition.connection ?? '', // device integration type (e.g. Flood Stage)
          selectedDevice: {
            id: condition.deviceId ?? '', // unique device ID
            node_id: condition.nodeId ?? '', // device node identifier
          },
          keyToCheck: condition?.value1?.value ?? '', // device data key to evaluate (e.g. data.depth1)
          operator: condition.operation ?? '', // comparison operator (==, !=, >, <, >=, <=)
          inputValueType: condition?.value2?.type === 'immediate', // true = static value, false = compare to another key
          valueToCompare:
            condition?.value2?.type === 'immediate' ? (condition?.value2?.value ?? '') : '', // static value to compare against
          keyToCompare: condition?.value2?.type === 'key' ? (condition?.value2?.value ?? '') : '', // secondary device key for comparison
        },
      };

    case 'deviceGroup':
      return {
        id: createNodeId(),
        category: 'Device Group',
        formData: {
          deviceType: condition.connection ?? '', // device integration type (e.g. Flood Stage)
          group: {
            id: condition.groupId ?? '', // device group ID
          },
          keyToCheck: condition?.value1?.value ?? '', // group device data key to evaluate
          operator: condition.operation ?? '', // comparison operator (==, !=, >, <, >=, <=)
          inputValueType: condition?.value2?.type === 'immediate', // true = static value, false = compare to another key
          valueToCompare:
            condition?.value2?.type === 'immediate' ? (condition?.value2?.value ?? '') : '', // static value to compare against
          keyToCompare: condition?.value2?.type === 'key' ? (condition?.value2?.value ?? '') : '', // secondary key for comparison
        },
      };
    case 'nwsalert':
      return {
        id: createNodeId(),
        category: 'NWS Alert',
        formData: {
          value: condition?.value?.value ?? '',
        },
      };
    case 'fusionalert':
      return {
        id: createNodeId(),
        category: 'Fusion Alert',
        formData: {
          selectedAlertType: condition?.value?.alertType ?? '',
        },
      };
    case 'triggerresolve':
      return {
        id: createNodeId(),
        category: 'Original Condition Resolved',
        formData: {},
      };
    default:
      return createEmptyCondition();
  }
};

// Loops through the nested triggers array and normalizes each condition group and condition within it (IF, AND, OR)
const normalizeConditions = (triggers) => {
  if (!Array.isArray(triggers) || triggers.length === 0) return [[createEmptyCondition()]];

  return triggers.map((conditionGroup) => {
    if (!Array.isArray(conditionGroup) || conditionGroup.length === 0)
      return [createEmptyCondition()];

    return conditionGroup.map((condition) => normalizeCondition(condition));
  });
};

// Creates an empty action object with a new ID and blank fields
export const createEmptyAction = () => {
  return {
    id: createNodeId(),
    category: '',
    formData: {},
  };
};

// Normalizes a single action based on its function
const normalizeAction = (action) => {
  if (!action || typeof action !== 'object') return createEmptyAction();

  const params = action.params ?? {};

  switch (action.function) {
    case 'ipaws':
      return {
        id: createNodeId(),
        category: 'Publish IPAWS Alert',
        formData: {
          ...(params.template?.message
            ? ipawsTemplateToFormData(params.template)
            : (params.authoringSnapshot ?? {})),
          femaEnvironment: readStoredIpawsEnvironment(params),
          intervalDuration: params.intervalDuration ?? 0,
          sent: Array.isArray(params.sent) ? params.sent : [],
        },
      };
    case 'deviceAction':
      return {
        id: createNodeId(),
        category: 'Device Action',
        formData: {
          ...params,
          selectedFunction: params.selectedFunction ?? params.actionType ?? '',
          node_id: params.node_id ?? params.nodeId ?? '',
        },
      };

    case 'deviceGroupAction':
      return {
        id: createNodeId(),
        category: 'Device Group Action',
        formData: {
          ...params,
          selectedFunction: params.selectedFunction ?? params.actionType ?? '',
          group_id: params.group_id ?? params.groupId ?? '',
        },
      };

    case 'sendEmail':
      return {
        id: createNodeId(),
        category: 'Send Email',
        formData: {
          ...params,
        },
      };

    case 'sendSMS':
      return {
        id: createNodeId(),
        category: 'Send SMS',
        formData: {
          ...params,
        },
      };

    default:
      return {
        id: createNodeId(),
        category: '',
        formData: {
          ...params,
        },
      };
  }
};

// Normalizes the THEN actions
const normalizeActions = (actions) => {
  if (!Array.isArray(actions) || actions.length === 0) return [];

  return actions.map((action) => normalizeAction(action));
};

// Normalizes the metadata fields (title, debounce time, min event time)
const normalizeMetadata = (initialData) => {
  return {
    title: initialData.title ?? '',
    debounceTime: Number(initialData.debounce_time ?? initialData.debounceTime ?? 0),
    minEventTime: Number(initialData.minEventTime ?? initialData.min_event_time ?? 0),
  };
};

// Loops through the nested resolutions array and normalizes each condition group and condition within it (UNTIL)
const normalizeUntil = (resolutions) => {
  if (!Array.isArray(resolutions) || resolutions.length === 0) return [];

  return resolutions.map((conditionGroup) => {
    if (!Array.isArray(conditionGroup) || conditionGroup.length === 0) return [];

    return conditionGroup.map((condition) => normalizeCondition(condition));
  });
};

export const normalizeInitialData = (initialData) => {
  if (!initialData) return getDefaultBuilderState();

  const savedBuilderState = getSavedBuilderState(initialData);

  return {
    id: initialData.id,
    metadata: normalizeMetadata(initialData),
    conditions: savedBuilderState?.conditions ?? normalizeConditions(initialData.triggers),
    actions: (savedBuilderState?.actions ?? normalizeActions(initialData.actions)).map(
      (action, index) =>
        action.category === 'Publish IPAWS Alert'
          ? {
              ...action,
              formData: {
                ...withStoredIpawsStatus(
                  withoutServiceOwnedTimes(
                    withStoredIpawsTemplate(action.formData, initialData.actions?.[index]?.params),
                  ),
                  initialData.actions?.[index]?.params?.approvalStatus,
                ),
                femaEnvironment: initialData.actions?.[index]?.params
                  ? readStoredIpawsEnvironment(initialData.actions[index].params)
                  : toFemaEnvironmentClass(action.formData.femaEnvironment),
                intervalDuration:
                  initialData.actions?.[index]?.params?.intervalDuration ??
                  action.formData.intervalDuration ??
                  0,
                sent: initialData.actions?.[index]?.params?.sent ?? action.formData.sent ?? [],
              },
            }
          : action,
    ),
    until: savedBuilderState?.until ?? normalizeUntil(initialData.resolutions),
  };
};

export const denormalizeNormalizedData = (normalizedData, globalActions = []) => {};
